import { useEffect, useId, useState } from "react"
import { CohortResults } from "@/schema"
import { useQueries } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import { fetchCohortResults } from "./api"

type ResultSummary = Pick<
  CohortResults,
  "result_id" | "columns" | "record_ids" | "record_count" | "row_count" | "description" | "record_type"
>

interface PageBookmark {
  cursor?: string
  start: number
  count?: number
}

// Bookmarks retain positions, never row payloads. Only pages around the viewport
// are observed; React Query discards their data as soon as they leave that window.
export function useCohortResultPages(cohortId: string | null) {
  const getContext = useApiContext()
  const sessionId = useId()
  const [summary, setSummary] = useState<ResultSummary>()
  const [bookmarks, setBookmarks] = useState<PageBookmark[]>([{ start: 0 }])
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 0 })
  const firstPage = Math.max(
    0,
    bookmarks.findLastIndex((page) => page.start <= visibleRange.start)
  )
  const lastPage = Math.max(
    firstPage,
    bookmarks.findLastIndex((page) => page.start <= visibleRange.end + 30)
  )
  const visibleLastPage = Math.max(
    firstPage,
    bookmarks.findLastIndex((page) => page.start <= visibleRange.end)
  )
  const windowStart = firstPage
  // Byte-limited pages can be short. Always cover the visible rows, even on a
  // tall screen, while limiting extra pages retained for prefetching.
  const windowEnd = Math.max(windowStart + 5, visibleLastPage + 1)
  const activePages = bookmarks.slice(windowStart, Math.min(windowEnd, lastPage + 1))
  const queries = useQueries({
    queries: activePages.map((page) => ({
      queryKey: ["cohort_result", cohortId, sessionId, page.cursor],
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const context = await getContext()
        if (!context) throw new Error("No API context")
        const result = await fetchCohortResults(context, cohortId!, page.cursor, summary?.result_id, signal)
        if (summary && result.row_count !== summary.row_count) {
          throw new Error("Report membership changed. Reopen the report to load it again.")
        }
        return result
      },
      enabled: !!cohortId,
      gcTime: 0,
      staleTime: Infinity,
      retry: false,
    })),
  })

  useEffect(() => {
    queries.forEach(({ data }, index) => {
      if (!data) return
      setSummary(
        (current) =>
          current ?? {
            result_id: data.result_id,
            columns: data.columns,
            record_ids: data.record_ids,
            record_count: data.record_count,
            row_count: data.row_count,
            description: data.description,
            record_type: data.record_type,
          }
      )
      const pageIndex = windowStart + index
      setBookmarks((current) => {
        if (current[pageIndex].count !== undefined) return current
        const updated = [...current]
        updated[pageIndex] = { ...current[pageIndex], count: data.rows.length }
        if (data.next_cursor && pageIndex === current.length - 1) {
          updated.push({ cursor: data.next_cursor, start: current[pageIndex].start + data.rows.length })
        }
        return updated
      })
    })
  }, [queries, windowStart])

  const last = bookmarks[bookmarks.length - 1]
  const error = queries.find((query) => query.error)?.error
  return {
    data: summary,
    error,
    isLoading: !summary && !error,
    isFetching: queries.some((query) => query.isFetching),
    rowCount: last.start + (last.count ?? 1),
    setVisibleRange,
    retry: () =>
      queries.forEach((query) => {
        if (query.error) void query.refetch()
      }),
    getRow: (index: number) => {
      const pageIndex = activePages.findLastIndex((page) => page.start <= index)
      const page = queries[pageIndex]?.data
      const offset = index - (activePages[pageIndex]?.start ?? 0)
      const values = page?.rows[offset]
      return values ? { values, recordId: page.row_ids[offset] } : undefined
    },
  }
}
